/**
 * useProperties Hook (NEW Architecture - Generic)
 * 
 * Thin wrapper around useList for properties.
 * All database operations through PropertyRepository.
 * Zero Firestore imports - fully database-agnostic!
 */

import { useMemo } from 'react';
import { useList } from './useList';
import { PropertyRepository } from '../models/PropertyRepository';
import { db, functions } from '../firebase';

/**
 * Hook for fetching and managing property lists
 * 
 * @param {Object} options - Filter and pagination options
 * @param {string} options.statusFilter - Filter by property status ('all', 'For Sale', etc.)
 * @param {string} options.sortOrder - Sort order ('asc' or 'desc')
 * @param {string} options.propertyTypeFilter - Filter by type ('all', 'scraped', 'test')
 * @param {string} options.deliveryStatusFilter - Filter by delivery status ('all', 'sent', 'delivered')
 * @param {string} options.mailshotId - Filter by mailshot ID
 * @param {string} options.scrapeId - Filter by scrape ID
 * @param {number} options.pageSize - Number of items per page (default: 500)
 * @param {boolean} options.realtime - Enable real-time updates (default: true)
 * 
 * @returns {Object} { properties, loading, error, refresh, count, isEmpty }
 */
export const useProperties = (options = {}) => {
  const {
    statusFilter = 'all',
    sortOrder = 'desc',
    propertyTypeFilter = 'all',
    deliveryStatusFilter = 'all',
    mailshotId = null,
    scrapeId = null,
    pageSize = 500,
    realtime = true
  } = options;

  // Create repository instance
  const repository = useMemo(() => new PropertyRepository(db, functions), []);

  // Build query options for repository
  const queryOptions = useMemo(() => {
    const whereConstraints = [];

    // Status filter
    if (statusFilter !== 'all') {
      whereConstraints.push(['propertyStatus', '==', statusFilter]);
    }

    // Property type filter
    if (propertyTypeFilter === 'scraped') {
      whereConstraints.push(['isTestProperty', '==', false]);
    } else if (propertyTypeFilter === 'test') {
      whereConstraints.push(['isTestProperty', '==', true]);
    }

    // Delivery status filter
    if (deliveryStatusFilter !== 'all') {
      whereConstraints.push(['deliveryStatus', '==', deliveryStatusFilter]);
    }

    // Mailshot filter
    if (mailshotId) {
      whereConstraints.push(['mailshots', 'array-contains', mailshotId]);
    }

    // Scrape filter
    if (scrapeId) {
      whereConstraints.push(['scrapeId', '==', scrapeId]);
    }

    return {
      where: whereConstraints.length > 0 ? whereConstraints : undefined,
      orderBy: 'dateAdded',
      orderDirection: sortOrder,
      limit: pageSize
    };
  }, [statusFilter, sortOrder, propertyTypeFilter, deliveryStatusFilter, mailshotId, scrapeId, pageSize]);

  // Use generic list hook
  const {
    entities: properties,
    loading,
    error,
    refresh,
    count,
    isEmpty
  } = useList({
    repository,
    options: queryOptions,
    realtime,
    immediate: true
  });

  return {
    properties,
    loading,
    error,
    refresh,
    count,
    isEmpty,
    hasError: !!error
  };
};
